import { type FC, useCallback, useMemo } from "react";
import { connect, type ConnectedProps } from "react-redux";

import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
  type FetchPageFn,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type ColumnOption } from "interfaces/columnOptions";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { type AppDispatch } from "features/Application/globaltypes/redux";

import { createLicensesAccountsFetcher } from "./Queries/queries";
import { type LicenseAccountRow } from "features/Reporting/types/packs";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";

import styles from "./packsReport.module.scss";

export type Props = {
  filter: PerformanceWithAccountFilter;
  onCustomerSelected: (row: LicenseAccountRow) => void;
};

type ConnectedPropsType = ConnectedProps<typeof connector>;

export const PacksReportMetricsByCustomerTable: FC<Props & ConnectedPropsType> = ({
  filter,
  onCustomerSelected,
  setHasExportData,
}) => {
  const fetchAccountsPage: FetchPageFn<LicenseAccountRow> = useMemo(
    () => createLicensesAccountsFetcher(filter),
    [filter],
  );

  const queryKeyBase = useMemo(() => ["LicensesAccounts", filter] as const, [filter]);

  const columnOptions = useMemo<ColumnOption<LicenseAccountRow>[]>(
    () => [
      {
        name: "Customer",
        width: 4,
        isSortable: true,
        sortKey: "CustomerName",
        render(entity) {
          return (
            <Tooltip
              target={
                <button className={styles.linkButton} onClick={() => onCustomerSelected(entity)}>
                  <TextTruncate className={styles.wordBreak}>{entity.CustomerName}</TextTruncate>
                </button>
              }
              content={entity.CustomerName}
              showAlways
            />
          );
        },
      },
      {
        name: "Free Packs",
        width: 3,
        isSortable: true,
        sortKey: "FreePacks",
        render: ({ FreePacks }) => FreePacks.toLocaleString(),
      },
      {
        name: "Other Packs",
        width: 3,
        isSortable: true,
        sortKey: "OtherPacks",
        render: ({ OtherPacks }) => OtherPacks.toLocaleString(),
      },
      {
        name: "Paid Packs",
        width: 3,
        isSortable: true,
        sortKey: "PaidPacks",
        render: ({ PaidPacks }) => PaidPacks.toLocaleString(),
      },
      {
        name: "Trial Packs",
        width: 3,
        isSortable: true,
        sortKey: "TrialPacks",
        render: ({ TrialPacks }) => TrialPacks.toLocaleString(),
      },
      {
        name: "Total Packs",
        width: 3,
        isSortable: true,
        sortKey: "TotalPacks",
        render: ({ TotalPacks }) => TotalPacks.toLocaleString(),
      },
    ],
    [onCustomerSelected],
  );

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. This packs report contains no data.",
      }),
    [setHasExportData],
  );

  return (
    <GenericPerformanceListWithServerPagination
      columns={columnOptions}
      filter={filter}
      fetchPage={fetchAccountsPage}
      queryKeyBase={queryKeyBase}
      hasDataForExport={updateExportDataState}
      isDateAllTime
    />
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
});

const connector = connect(null, mapDispatchToProps);

const ConnectedComponent = connector(PacksReportMetricsByCustomerTable);
export default ConnectedComponent;
